import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Repeat, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
const FREQ_LABELS = {
  daily: 'Daily', weekly: 'Weekly', monthly: 'Monthly',
  every_2_months: 'Every 2 months', every_3_months: 'Every 3 months',
};
const EMPTY = { title: '', category_id: '', amount: '', frequency: 'monthly', start_date: '', vendor_name: '', payment_mode_id: '', description: '' };

export const RecurringExpensesView = () => {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [modes, setModes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const [r, c, m] = await Promise.all([
        axios.get(`${API}/recurring-expenses`, auth()),
        axios.get(`${API}/expenses/categories`, auth()),
        axios.get(`${API}/expenses/payment-modes`, auth()).catch(() => ({ data: [] })),
      ]);
      setItems(r.data.items || []);
      setCategories(c.data.categories || c.data || []);
      setModes(m.data.payment_modes || m.data || []);
    } catch { toast.error('Failed to load recurring expenses'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.title.trim() || !form.category_id || !(parseFloat(form.amount) > 0)) {
      toast.error('Title, category and a positive amount are required'); return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/recurring-expenses`, {
        ...form, amount: parseFloat(form.amount),
        start_date: form.start_date || undefined,
        payment_mode_id: form.payment_mode_id || undefined,
      }, auth());
      toast.success('Recurring expense created');
      setDialogOpen(false); setForm(EMPTY); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not create'); }
    finally { setSaving(false); }
  };

  const toggleActive = async (it) => {
    try {
      await axios.put(`${API}/recurring-expenses/${it.id}`, { active: !it.active }, auth());
      setItems((rows) => rows.map((r) => (r.id === it.id ? { ...r, active: !it.active } : r)));
      toast.success(!it.active ? 'Resumed' : 'Paused');
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not update'); }
  };

  const remove = async (it) => {
    if (!window.confirm(`Delete recurring expense "${it.title}"? Already-created expenses stay.`)) return;
    try { await axios.delete(`${API}/recurring-expenses/${it.id}`, auth()); toast.success('Deleted'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not delete'); }
  };

  return (
    <div className="space-y-4" data-testid="recurring-expenses-view">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="text-sm text-wt-text-muted">Expenses created automatically on schedule. Added by an admin → posts as <b>approved</b>; added by other staff → needs <b>approval every time</b>.</p>
        <Button onClick={() => setDialogOpen(true)} className="bg-wt-gold hover:bg-wt-gold-hover text-black" data-testid="new-recurring-expense-btn">
          <Plus className="h-4 w-4 mr-1" /> New Recurring Expense
        </Button>
      </div>

      <div className="rounded-xl border border-wt-border bg-wt-paper overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-wt-text-muted border-b border-wt-border">
              <th className="p-3">Title</th><th className="p-3">Category</th>
              <th className="p-3 text-right">Amount</th><th className="p-3">Frequency</th>
              <th className="p-3">Next Run</th><th className="p-3">Posts As</th>
              <th className="p-3">Created By</th><th className="p-3 text-right">Runs</th>
              <th className="p-3">Active</th><th className="p-3"></th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={10} className="p-6 text-center text-wt-text-muted">Loading…</td></tr>}
            {!loading && items.length === 0 && (
              <tr><td colSpan={10} className="p-6 text-center text-wt-text-muted" data-testid="recurring-empty">No recurring expenses yet — add rent, subscriptions, salaries…</td></tr>
            )}
            {items.map((it) => (
              <tr key={it.id} className="border-b border-wt-border/50" data-testid={`recurring-row-${it.id}`}>
                <td className="p-3 text-wt-text-primary">{it.title}{it.vendor_name ? <span className="block text-xs text-wt-text-muted">{it.vendor_name}</span> : null}</td>
                <td className="p-3 text-wt-text-secondary">{it.category_name}</td>
                <td className="p-3 text-right text-wt-text-primary font-mono">AED {Number(it.amount).toFixed(2)}</td>
                <td className="p-3"><span className="inline-flex items-center gap-1 text-wt-text-secondary"><Repeat className="h-3 w-3 text-wt-gold" />{FREQ_LABELS[it.frequency] || it.frequency}</span></td>
                <td className="p-3 text-wt-text-secondary">{it.next_run}</td>
                <td className="p-3">
                  <span className={`px-2 py-0.5 rounded text-xs ${it.created_by_is_admin ? 'bg-emerald-500/15 text-emerald-400' : 'bg-amber-500/15 text-amber-400'}`}>
                    {it.created_by_is_admin ? 'approved' : 'needs approval'}
                  </span>
                </td>
                <td className="p-3 text-wt-text-secondary">{it.created_by_name}</td>
                <td className="p-3 text-right text-wt-text-muted">{it.runs_count || 0}</td>
                <td className="p-3"><Switch checked={!!it.active} onCheckedChange={() => toggleActive(it)} data-testid={`recurring-active-${it.id}`} /></td>
                <td className="p-3"><Button size="icon" variant="ghost" onClick={() => remove(it)} data-testid={`recurring-del-${it.id}`}><Trash2 className="h-4 w-4 text-red-400" /></Button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg bg-wt-paper border-wt-border">
          <DialogHeader><DialogTitle className="text-wt-text-primary">New Recurring Expense</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Title</Label><Input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} placeholder="e.g. Office Rent — Sharjah" data-testid="recurring-title-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Category</Label>
                <Select value={form.category_id} onValueChange={(v) => setForm((f) => ({ ...f, category_id: v }))}>
                  <SelectTrigger data-testid="recurring-category-select"><SelectValue placeholder="Select category" /></SelectTrigger>
                  <SelectContent>{categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Amount (AED)</Label><Input type="number" min="0" step="0.01" value={form.amount} onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))} data-testid="recurring-amount-input" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Frequency</Label>
                <Select value={form.frequency} onValueChange={(v) => setForm((f) => ({ ...f, frequency: v }))}>
                  <SelectTrigger data-testid="recurring-frequency-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(FREQ_LABELS).map(([v, l]) => <SelectItem key={v} value={v} data-testid={`recurring-freq-${v}`}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>First run date</Label><Input type="date" value={form.start_date} onChange={(e) => setForm((f) => ({ ...f, start_date: e.target.value }))} data-testid="recurring-start-input" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Vendor (optional)</Label><Input value={form.vendor_name} onChange={(e) => setForm((f) => ({ ...f, vendor_name: e.target.value }))} data-testid="recurring-vendor-input" /></div>
              <div>
                <Label>Payment mode (optional)</Label>
                <Select value={form.payment_mode_id || 'none'} onValueChange={(v) => setForm((f) => ({ ...f, payment_mode_id: v === 'none' ? '' : v }))}>
                  <SelectTrigger data-testid="recurring-mode-select"><SelectValue placeholder="None" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {modes.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>Notes (optional)</Label><Input value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} data-testid="recurring-notes-input" /></div>
            <Button onClick={save} disabled={saving} className="w-full bg-wt-gold hover:bg-wt-gold-hover text-black" data-testid="recurring-save-btn">
              {saving ? 'Saving…' : 'Create Recurring Expense'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
